<template>
    <button
        :type="type"
        :class="[
            'inline-flex items-center justify-center gap-1 rounded-lg font-medium transition focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 focus-visible:outline-none dark:focus-visible:ring-white dark:focus-visible:ring-offset-gray-900 pointer-coarse:min-h-11',
            !variant?.startsWith('icon') ? sizeClasses[size || 'sm'].button : '',
            variant ? variantClasses[variant] : '',
            fullWidth ? 'w-full' : '',
            className,
            { 'cursor-not-allowed opacity-50': isInactive },
        ]"
        @click="
            (e) => {
                if (stopPropagation) {
                    e.stopPropagation();
                }
                if (isInactive) {
                    e.preventDefault();
                    return;
                }
                onClick();
            }
        "
        :disabled="isInactive"
        :aria-busy="loading || undefined"
    >
        <!--
            While loading the spinner takes the place of the leading icon. An
            icon-only button has no label to keep, so there the spinner replaces
            the icon in the default slot instead.
        -->
        <svg
            v-if="loading"
            :class="['shrink-0 animate-spin', sizeClasses[size || 'sm'].spinner]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
            data-cy="button-spinner"
        >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
        </svg>
        <span v-else-if="$slots['start-icon'] || startIcon" class="flex items-center">
            <slot name="start-icon">
                <component :is="startIcon" v-if="startIcon" />
            </slot>
        </span>
        <slot v-if="!isLoadingIconButton"></slot>
        <span v-if="$slots['end-icon'] || endIcon" class="flex items-center">
            <slot name="end-icon">
                <component :is="endIcon" v-if="endIcon" />
            </slot>
        </span>
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface ButtonProps {
    type?: 'button' | 'submit' | 'reset';
    size?: 'xs' | 'sm' | 'md';
    variant?:
        | 'primary'
        | 'outline'
        | 'secondary'
        | 'icon'
        | 'icon-danger'
        | 'danger'
        | 'info'
        | 'link'
        | 'link-muted'
        | 'success'
        | 'dark';
    startIcon?: object;
    endIcon?: object;
    onClick?: () => void;
    className?: string;
    disabled?: boolean;
    /** Shows a spinner and blocks interaction while an action is in flight. */
    loading?: boolean;
    stopPropagation?: boolean;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
    type: 'button',
    size: 'sm',
    variant: 'primary',
    className: '',
    disabled: false,
    loading: false,
    stopPropagation: false,
    fullWidth: false,
});

const sizeClasses = {
    xs: { button: 'px-4 py-2 text-xs', spinner: 'h-3.5 w-3.5' },
    sm: { button: 'px-4 py-2.5 text-sm', spinner: 'h-4 w-4' },
    md: { button: 'px-4 py-3 text-lg', spinner: 'h-5 w-5' },
};

// A loading button is inert: every call site that passes `loading` means "this
// action is already running", so it must not be submitted twice.
const isInactive = computed(() => props.disabled || props.loading);
const isLoadingIconButton = computed(() => props.loading && !!props.variant?.startsWith('icon'));

const variantClasses = {
    icon: 'rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white pointer-coarse:min-h-11 pointer-coarse:min-w-11',
    'icon-danger':
        'rounded-lg p-1.5 text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-500/10 dark:hover:text-error-300 pointer-coarse:min-h-11 pointer-coarse:min-w-11',
    primary: 'bg-brand-500 text-gray-900 shadow-theme-xs hover:bg-brand-600 dark:hover:bg-brand-300',
    secondary:
        'bg-white text-gray-700 ring-1 ring-inset ring-gray-200 shadow-theme-xs hover:bg-gray-50 hover:ring-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700',
    success:
        'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200 hover:bg-green-100 hover:ring-green-300 dark:bg-green-500/[0.12] dark:text-green-400 dark:ring-green-500/30 dark:hover:bg-green-500/20',
    danger: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100 hover:ring-red-300 dark:bg-red-500/[0.12] dark:text-red-400 dark:ring-red-500/30 dark:hover:bg-red-500/20',
    info: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-100 hover:ring-blue-300 dark:bg-blue-500/[0.12] dark:text-blue-400 dark:ring-blue-500/30 dark:hover:bg-blue-500/20',
    // !items-center, not !items-start: a link button is still a flex row, so
    // start-alignment pushed a start-icon to the top of the line box instead of
    // centring it on the label, and on touch (pointer-coarse:min-h-11) it stuck
    // the label to the top of the 44px box. Only !justify-start is wanted here,
    // to keep a full-width link left-aligned.
    link: 'bg-transparent text-brand-700 underline hover:no-underline dark:text-brand-400 !px-0 !items-center !justify-start',
    'link-muted':
        'bg-transparent text-gray-600 underline hover:no-underline dark:text-gray-400 !px-0 !items-center !justify-start',
    outline:
        'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-brand-50 hover:ring-brand-300 hover:text-brand-700 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300',
    dark: 'bg-gray-900 text-white shadow-theme-xs hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white',
};

const onClick = () => {
    if (!isInactive.value && props.onClick) {
        props.onClick();
    }
};
</script>
